import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createOrder, getCatalogTypes, getCatalogDemos, getSubmissionStatus, checkPromoCode } from '../api';
import { DemoThumbnail, FullscreenPreview } from './shared/DemoPreview';

/* ═══════════════════════════════════════════
   ORDER PAGE — /comanda
   Reached from a generated demo (?submission=…&variant=…), from the price cards
   (?package=…) or directly. Clients pick a model, a package and leave their details.
   ═══════════════════════════════════════════ */

const PACKAGES = [
  { id: 'landing', amount: 1000, name: 'Landing Page', price: '1.000', color: '#8be9fd', desc: 'O pagină, tot ce trebuie ca oamenii să te găsească și să te sune.' },
  { id: 'prezentare', amount: 1300, name: 'Site Prezentare', price: '1.300', color: '#50fa7b', desc: 'Mai multe pagini: servicii, galerie, despre, contact.', popular: true },
  { id: 'magazin', amount: 3000, name: 'Magazin Online', price: '3.000', color: '#ff79c6', desc: 'Produse, coș, comenzi și plăți online.' },
  { id: 'custom', amount: 3500, name: 'Aplicație Custom', price: '3.500', color: '#bd93f9', desc: 'Programări, rezervări, conturi — ce are nevoie afacerea ta.' },
];

const MAINTENANCE_PRICE = 150;
const ron = (n) => n.toLocaleString('ro-RO');

// Same rules as the backend (api/pricing.py); the server re-checks the code when the order is sent
const quoteFor = (pkgId, maint, promo) => {
  const price = PACKAGES.find(p => p.id === pkgId).amount;
  let pkgOff = 0, maintOff = 0;
  if (promo) {
    const covers = (svc) => !promo.services?.length || promo.services.includes(svc);
    const coversPkg = covers(pkgId), coversMaint = maint && covers('mentenanta');
    if (promo.discount_type === 'percent') {
      const pct = Math.min(promo.value, 100);
      if (coversPkg) pkgOff = Math.round(price * pct / 100);
      if (coversMaint) maintOff = Math.round(MAINTENANCE_PRICE * pct / 100);
    } else if (coversPkg) pkgOff = Math.min(promo.value, price); // fixed amounts never come off the monthly fee
  }
  return { price, pkgFinal: price - pkgOff, maintFinal: MAINTENANCE_PRICE - maintOff, applies: !!(pkgOff || maintOff) };
};

function promoServices(promo) {
  return (promo.services || [])
    .filter(s => promo.discount_type === 'percent' || s !== 'mentenanta')
    .map(s => SERVICE_NAMES[s] || s).join(', ');
}

const SERVICE_NAMES = { landing: 'Landing Page', prezentare: 'Site Prezentare', magazin: 'Magazin Online', custom: 'Aplicație Custom', mentenanta: 'Mentenanță' };

// Ads can link to any page with ?cod=XYZ; App.jsx keeps it for the order page
const storedPromo = () => {
  try { return sessionStorage.getItem('dd_promo') || ''; } catch { return ''; }
};

const STYLE_COLORS = ['#50fa7b', '#ff79c6', '#bd93f9'];
const STYLE_LABELS = ['Modern & Minimalist', 'Bold & Colorful', 'Classic & Professional'];

const font = "'Space Grotesk', sans-serif";
const mono = "'JetBrains Mono', monospace";

const StepTitle = ({ n, children, sub }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{
        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#bd93f920', border: '1px solid #bd93f950', color: '#bd93f9',
        fontFamily: mono, fontSize: 13, fontWeight: 700,
      }}>{n}</span>
      <h2 style={{ fontFamily: font, fontSize: 'clamp(19px,3vw,24px)', color: '#f8f8f2', fontWeight: 700, margin: 0 }}>{children}</h2>
    </div>
    {sub && <p style={{ color: '#6272a4', fontSize: 13, margin: '8px 0 0 42px', fontFamily: font, lineHeight: 1.5 }}>{sub}</p>}
  </div>
);

const ModelCard = ({ html, title, subtitle, color, selected, onPreview, onSelect }) => (
  <div style={{
    background: '#111218', borderRadius: 12, overflow: 'hidden',
    border: `1px solid ${selected ? color : '#1e2030'}`,
    boxShadow: selected ? `0 0 0 2px ${color}55, 0 12px 40px ${color}20` : '0 4px 20px rgba(0,0,0,0.3)',
    transition: 'all 0.25s',
  }}>
    <div onClick={onPreview} style={{ position: 'relative', height: 'clamp(170px,40vw,220px)', cursor: 'pointer', background: '#16181e' }}>
      <DemoThumbnail html={html} title={`Previzualizare ${title}`} />
      {selected && (
        <span style={{
          position: 'absolute', top: 10, right: 10, background: color, color: '#0d0e12',
          borderRadius: 20, padding: '4px 12px', fontSize: 11, fontWeight: 700, fontFamily: font,
        }}>✓ Ales</span>
      )}
    </div>
    <div style={{ padding: '12px 14px', borderTop: '1px solid #1a1c28' }}>
      <div style={{ fontFamily: font, fontSize: 14, fontWeight: 600, color: '#f8f8f2' }}>{title}</div>
      <div style={{ fontFamily: mono, fontSize: 10, color: '#6272a4', marginTop: 2 }}>{subtitle}</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" onClick={onPreview} style={{
          flex: 1, background: 'transparent', border: '1px solid #282a36', borderRadius: 8,
          padding: '8px 10px', color: '#8be9fd', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: font,
        }}>🔍 Vezi</button>
        <button type="button" onClick={onSelect} style={{
          flex: 1.4, border: 'none', borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
          background: selected ? color : `${color}20`, color: selected ? '#0d0e12' : color,
          fontSize: 12, fontWeight: 700, fontFamily: font,
        }}>{selected ? '✓ Ales' : 'Alege modelul'}</button>
      </div>
    </div>
  </div>
);

const inputStyle = {
  width: '100%', boxSizing: 'border-box', background: '#0d0e12', border: '1px solid #282a36',
  borderRadius: 8, padding: '12px 14px', color: '#f8f8f2', fontSize: 15, fontFamily: font, outline: 'none',
};

const Field = ({ label, required, children }) => (
  <label style={{ display: 'block' }}>
    <span style={{ display: 'block', color: '#8b8fa3', fontSize: 12, marginBottom: 6, fontFamily: font }}>
      {label}{required && <span style={{ color: '#ff79c6' }}> *</span>}
    </span>
    {children}
  </label>
);

const OrderPage = () => {
  const [params] = useSearchParams();
  const submissionId = params.get('submission');
  const initialPackage = PACKAGES.some(p => p.id === params.get('package')) ? params.get('package') : 'prezentare';

  const [submission, setSubmission] = useState(null);
  const [types, setTypes] = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [catalog, setCatalog] = useState({}); // business_type -> demos
  const [catalogError, setCatalogError] = useState('');
  const [preview, setPreview] = useState(null); // { title, badge, html, choice }

  // choice: { kind: 'own', variant } | { kind: 'catalog', key, title } | null (fără model)
  const [choice, setChoice] = useState(() => {
    const model = params.get('model');
    return model ? { kind: 'catalog', key: model, title: model } : null;
  });
  const [pkg, setPkg] = useState(initialPackage);
  const [maintenance, setMaintenance] = useState(params.get('maintenance') === '1');
  const [form, setForm] = useState({ name: '', email: '', phone: '', business_name: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [promoInput, setPromoInput] = useState(() => params.get('cod') || storedPromo());
  const [promo, setPromo] = useState(null); // { code, label, discount_type, value, services }
  const [promoError, setPromoError] = useState('');
  const [promoChecking, setPromoChecking] = useState(false);
  const packagesRef = useRef(null);

  // The client's own demos (coming from the demo generator)
  useEffect(() => {
    if (!submissionId) return;
    getSubmissionStatus(submissionId).then(data => {
      setSubmission(data);
      setForm(f => ({ ...f, name: f.name || data.name || '', email: f.email || data.email || '', business_name: f.business_name || data.business_name || '' }));
      const variant = Number(params.get('variant'));
      if ((data.demos || []).some(d => d.variant_number === variant)) setChoice({ kind: 'own', variant });
      if (data.business_type) setActiveType(t => t || data.business_type);
    }).catch(() => setSubmission(null));
  }, [submissionId]); // eslint-disable-line

  useEffect(() => {
    getCatalogTypes().then(list => {
      setTypes(list);
      const fromModel = (params.get('model') || '').replace(/-\d+$/, '');
      setActiveType(t => t || (list.some(x => x.business_type === fromModel) ? fromModel : list[0]?.business_type));
    }).catch(() => setCatalogError('Nu am putut încărca modelele. Poți comanda și fără să alegi unul.'));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!activeType || catalog[activeType]) return;
    getCatalogDemos(activeType)
      .then(data => setCatalog(c => ({ ...c, [activeType]: data.demos })))
      .catch(() => setCatalog(c => ({ ...c, [activeType]: [] })));
  }, [activeType, catalog]);

  const pick = (next) => {
    setChoice(next);
    setPreview(null);
    setTimeout(() => packagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const isChosen = (c) => choice && choice.kind === c.kind &&
    (c.kind === 'own' ? choice.variant === c.variant : choice.key === c.key);

  const choiceLabel = !choice
    ? 'Fără model — pornim de la zero împreună'
    : choice.kind === 'own'
      ? `Demo-ul tău, varianta ${choice.variant}`
      : `Model din galerie: ${choice.title}`;

  const applyPromo = async (code = promoInput) => {
    const clean = code.trim();
    if (!clean) return;
    setPromoChecking(true);
    setPromoError('');
    try {
      const data = await checkPromoCode({ code: clean, package: pkg, maintenance });
      setPromo(data);
      setPromoInput(data.code);
    } catch (err) {
      setPromo(null);
      setPromoError(err.response?.status === 429
        ? 'Prea multe încercări. Mai încearcă peste puțin timp.'
        : err.response?.data?.error || 'Nu am putut verifica codul. Încearcă din nou.');
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromo = () => {
    setPromo(null);
    setPromoInput('');
    setPromoError('');
    try { sessionStorage.removeItem('dd_promo'); } catch { /* storage unavailable */ }
  };

  // A code that came from an ad link is applied straight away
  useEffect(() => {
    if (promoInput) applyPromo(promoInput);
  }, []); // eslint-disable-line

  const q = quoteFor(pkg, maintenance, promo);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = 'Scrie-ne numele tău.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = 'Adresa de email nu pare corectă.';
    if (form.phone.replace(/\D/g, '').length < 9) errs.phone = 'Lasă-ne un număr de telefon ca să te putem suna.';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSending(true);
    try {
      await createOrder({
        ...form,
        package: pkg,
        maintenance,
        submission: choice?.kind === 'own' ? submissionId : (submission ? submissionId : null),
        variant_number: choice?.kind === 'own' ? choice.variant : null,
        catalog_key: choice?.kind === 'catalog' ? choice.key : '',
        promo_code: promo && q.applies ? promo.code : '',
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 429) setErrors({ general: 'Prea multe încercări. Mai încearcă peste puțin timp sau scrie-ne la contact@draculadev.com.' });
      else if (data && typeof data === 'object') {
        const flat = {};
        Object.entries(data).forEach(([k, v]) => { flat[k] = Array.isArray(v) ? v.join(' ') : String(v); });
        setErrors({ ...flat, general: 'Verifică datele de mai jos.' });
      } else setErrors({ general: 'Ceva n-a mers. Încearcă din nou sau scrie-ne la contact@draculadev.com.' });
    } finally {
      setSending(false);
    }
  };

  const selectedPkg = PACKAGES.find(p => p.id === pkg);

  if (done) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0b0f', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{
          maxWidth: 520, width: '100%', textAlign: 'center', background: '#111218', border: '1px solid #50fa7b30',
          borderRadius: 16, padding: 'clamp(28px,7vw,44px) clamp(18px,5vw,36px)',
        }}>
          <div style={{
            width: 60, height: 60, borderRadius: '50%', margin: '0 auto 18px', fontSize: 28, color: '#50fa7b',
            background: '#50fa7b15', border: '2px solid #50fa7b40', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✓</div>
          <h1 style={{ fontFamily: font, color: '#f8f8f2', fontSize: 'clamp(22px,4vw,28px)', margin: '0 0 10px' }}>Comanda a fost trimisă!</h1>
          <p style={{ color: '#8b8fa3', fontFamily: font, fontSize: 15, lineHeight: 1.6, margin: '0 0 8px' }}>
            Mulțumim, {form.name.split(' ')[0]}! Te sunăm în cel mult o zi lucrătoare la <span style={{ color: '#f1fa8c' }}>{form.phone}</span> ca să stabilim detaliile.
          </p>
          <p style={{ color: '#6272a4', fontFamily: font, fontSize: 13, lineHeight: 1.6, margin: '0 0 24px' }}>
            Ți-am trimis și un email de confirmare la {form.email}. Nu plătești nimic până nu stabilim împreună ce și cum facem.
          </p>
          <Link to="/" style={{
            display: 'inline-block', background: 'linear-gradient(135deg, #bd93f9, #ff79c6)', color: '#fff',
            borderRadius: 10, padding: '12px 28px', fontWeight: 700, fontFamily: font, textDecoration: 'none', fontSize: 14,
          }}>Înapoi la DraculaDev</Link>
        </div>
      </div>
    );
  }

  const catalogDemos = catalog[activeType];
  const typeLabel = types.find(t => t.business_type === activeType)?.label;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0b0f', color: '#f8f8f2' }}>
      <style>{`
        .order-input:focus { border-color: #bd93f9 !important; }
        .order-chips { display: flex; gap: 8px; flex-wrap: wrap; }
        @media (max-width: 640px) {
          .order-chips { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 6px; margin: 0 -16px; padding-left: 16px; padding-right: 16px; scrollbar-width: none; }
          .order-chips::-webkit-scrollbar { display: none; }
          .order-chips button { flex-shrink: 0; }
          .order-form-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Top bar ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50, background: 'rgba(10,11,15,0.85)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #1e2030',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Link to="/" style={{ color: '#f8f8f2', textDecoration: 'none', fontFamily: font, fontWeight: 700, fontSize: 16 }}>
            🧛 Dracula<span style={{ color: '#bd93f9' }}>Dev</span>
          </Link>
          <Link to="/" style={{ color: '#6272a4', textDecoration: 'none', fontFamily: mono, fontSize: 12 }}>← Înapoi</Link>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(28px,6vw,56px) 16px 64px' }}>
        {/* ── Header ── */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(32px,6vw,52px)' }}>
          <div style={{
            display: 'inline-block', background: '#50fa7b12', border: '1px solid #50fa7b25', borderRadius: 20,
            padding: '5px 16px', marginBottom: 16, color: '#50fa7b', fontFamily: mono, fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
          }}>COMANDĂ SITE-UL TĂU</div>
          <h1 style={{ fontFamily: font, fontSize: 'clamp(28px,5vw,44px)', fontWeight: 700, margin: '0 0 12px', lineHeight: 1.15 }}>
            Alegi modelul, noi îl facem <span style={{
              background: 'linear-gradient(135deg, #bd93f9, #ff79c6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>al tău</span>
          </h1>
          <p style={{ color: '#8b8fa3', fontFamily: font, fontSize: 'clamp(14px,2vw,16px)', maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}>
            Nu plătești nimic acum. Trimiți comanda, te sunăm, stabilim detaliile și abia apoi ne apucăm de treabă.
          </p>
        </div>

        {/* ── 1. Model ── */}
        <section style={{ marginBottom: 48 }}>
          <StepTitle n={1} sub="Modelul e doar punctul de plecare — textele, pozele și culorile le adaptăm afacerii tale.">
            Alege un model
          </StepTitle>

          {submission && (submission.demos || []).length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ fontFamily: font, fontSize: 15, color: '#50fa7b', margin: '0 0 14px' }}>
                ✨ Demo-urile generate pentru {submission.business_name || submission.name}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px,100%),1fr))', gap: 18 }}>
                {submission.demos.map((d, i) => {
                  const c = { kind: 'own', variant: d.variant_number };
                  return (
                    <ModelCard key={d.id} html={d.html_content} title={STYLE_LABELS[i] || d.title}
                      subtitle={`Varianta ${d.variant_number}`} color={STYLE_COLORS[i] || '#bd93f9'}
                      selected={isChosen(c)}
                      onPreview={() => setPreview({ title: d.title, badge: `V${d.variant_number}`, html: d.html_content, choice: c })}
                      onSelect={() => pick(c)} />
                  );
                })}
              </div>
            </div>
          )}

          <h3 style={{ fontFamily: font, fontSize: 15, color: '#8be9fd', margin: '0 0 14px' }}>
            {submission ? 'Sau alege din galeria noastră' : 'Galeria de modele'}
          </h3>
          {catalogError && <p style={{ color: '#ffb86c', fontFamily: font, fontSize: 13 }}>{catalogError}</p>}
          <div className="order-chips" style={{ marginBottom: 18 }}>
            {types.map(t => {
              const active = t.business_type === activeType;
              return (
                <button key={t.business_type} type="button" onClick={() => setActiveType(t.business_type)} style={{
                  background: active ? '#bd93f9' : '#16181e', color: active ? '#0d0e12' : '#8b8fa3',
                  border: `1px solid ${active ? '#bd93f9' : '#282a36'}`, borderRadius: 20, padding: '7px 14px',
                  fontSize: 13, fontWeight: 600, fontFamily: font, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>{t.label}</button>
              );
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px,100%),1fr))', gap: 18, minHeight: 120 }}>
            {!catalogDemos && activeType && (
              <p style={{ color: '#6272a4', fontFamily: mono, fontSize: 12 }}>Se încarcă modelele…</p>
            )}
            {(catalogDemos || []).map((d, i) => {
              const title = `${typeLabel} — ${STYLE_LABELS[i] || d.style}`;
              const c = { kind: 'catalog', key: d.key, title };
              return (
                <ModelCard key={d.key} html={d.html} title={STYLE_LABELS[i] || d.style} subtitle={d.title.split(' — ')[0]}
                  color={STYLE_COLORS[i] || '#bd93f9'} selected={isChosen(c)}
                  onPreview={() => setPreview({ title: d.title, badge: STYLE_LABELS[i], html: d.html, choice: c })}
                  onSelect={() => pick(c)} />
              );
            })}
          </div>
          <button type="button" onClick={() => pick(null)} style={{
            marginTop: 18, background: !choice ? '#f1fa8c18' : 'transparent', border: `1px dashed ${!choice ? '#f1fa8c' : '#44475a'}`,
            color: !choice ? '#f1fa8c' : '#8b8fa3', borderRadius: 10, padding: '12px 18px', width: '100%',
            fontFamily: font, fontSize: 13, cursor: 'pointer',
          }}>
            {!choice ? '✓ ' : ''}Nu mă leg de niciun model — vreau ceva făcut de la zero
          </button>
        </section>

        {/* ── 2. Package ── */}
        <section ref={packagesRef} style={{ marginBottom: 48, scrollMarginTop: 80 }}>
          <StepTitle n={2} sub="Prețurile sunt de pornire. Prețul final îl stabilim împreună, înainte să începem.">Alege pachetul</StepTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px,100%),1fr))', gap: 14 }}>
            {PACKAGES.map(p => {
              const active = p.id === pkg;
              const pq = quoteFor(p.id, false, promo);
              return (
                <button key={p.id} type="button" onClick={() => setPkg(p.id)} style={{
                  textAlign: 'left', cursor: 'pointer', position: 'relative',
                  background: active ? `${p.color}12` : '#111218', borderRadius: 12, padding: '18px 18px 16px',
                  border: `1px solid ${active ? p.color : '#1e2030'}`, boxShadow: active ? `0 0 0 1px ${p.color}` : 'none',
                  transition: 'all 0.2s', fontFamily: font,
                }}>
                  {p.popular && (
                    <span style={{
                      position: 'absolute', top: 10, right: 10, fontSize: 9, fontFamily: mono, fontWeight: 700,
                      color: '#50fa7b', background: '#50fa7b15', borderRadius: 10, padding: '2px 8px',
                    }}>POPULAR</span>
                  )}
                  <div style={{ color: active ? p.color : '#f8f8f2', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                    {active ? '● ' : '○ '}{p.name}
                  </div>
                  <div style={{ color: '#f8f8f2', fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: '#6272a4', fontWeight: 400 }}>de la </span>
                    {pq.applies && <s style={{ fontSize: 14, color: '#6272a4', fontWeight: 500, marginRight: 6 }}>{p.price}</s>}
                    <span style={{ color: pq.applies ? '#ff79c6' : undefined }}>{ron(pq.pkgFinal)}</span>{' '}
                    <span style={{ fontSize: 13, color: '#6272a4' }}>RON</span>
                  </div>
                  <div style={{ color: '#8b8fa3', fontSize: 12, lineHeight: 1.5 }}>{p.desc}</div>
                </button>
              );
            })}
          </div>
          <label style={{
            display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 14, cursor: 'pointer',
            background: '#111218', border: `1px solid ${maintenance ? '#f1fa8c60' : '#1e2030'}`, borderRadius: 12, padding: '14px 16px',
          }}>
            <input type="checkbox" checked={maintenance} onChange={e => setMaintenance(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: '#f1fa8c', marginTop: 2, flexShrink: 0 }} />
            <span style={{ fontFamily: font }}>
              <span style={{ color: '#f8f8f2', fontWeight: 600, fontSize: 14 }}>
                Adaug mentenanță — {quoteFor(pkg, true, promo).maintFinal < MAINTENANCE_PRICE
                  ? <><s style={{ color: '#6272a4', fontWeight: 400 }}>150</s> <span style={{ color: '#ff79c6' }}>{quoteFor(pkg, true, promo).maintFinal}</span></>
                  : '150'} RON/lună
              </span>
              <span style={{ display: 'block', color: '#6272a4', fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>
                Hosting, actualizări, backup și modificări mici făcute de noi. Poți renunța oricând.
              </span>
            </span>
          </label>

          {/* Promo code */}
          <div style={{
            marginTop: 14, background: '#111218', borderRadius: 12, padding: '14px 16px',
            border: `1px solid ${promo ? (q.applies ? '#ff79c660' : '#ffb86c60') : '#1e2030'}`,
          }}>
            {promo ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontFamily: font }}>
                <span style={{ fontSize: 20 }}>🎁</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ color: '#f8f8f2', fontWeight: 600, fontSize: 14 }}>
                    Codul <span style={{ color: '#ff79c6', fontFamily: mono }}>{promo.code}</span> — reducere {promo.label}
                  </div>
                  <div style={{ color: q.applies ? '#6272a4' : '#ffb86c', fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>
                    {q.applies
                      ? (promo.services?.length ? `Se aplică la: ${promoServices(promo)}.` : 'Se aplică la toate serviciile.')
                      : `Nu se aplică pentru ce ai ales acum. E valabil pentru: ${promoServices(promo)}.`}
                  </div>
                </div>
                <button type="button" onClick={removePromo} style={{
                  background: 'transparent', border: '1px solid #282a36', borderRadius: 8, padding: '6px 12px',
                  color: '#8b8fa3', fontSize: 12, cursor: 'pointer', fontFamily: font,
                }}>Elimină</button>
              </div>
            ) : (
              <form onSubmit={e => { e.preventDefault(); applyPromo(); }} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ color: '#f8f8f2', fontWeight: 600, fontSize: 14, fontFamily: font, flex: '1 1 100%' }}>🎁 Ai un cod de reducere?</span>
                <input className="order-input" value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="EX: TOAMNA10" aria-label="Cod de reducere" autoCapitalize="characters" spellCheck={false}
                  style={{ ...inputStyle, flex: '1 1 180px', width: 'auto', fontFamily: mono, letterSpacing: 1, padding: '10px 14px' }} />
                <button type="submit" disabled={promoChecking || !promoInput.trim()} style={{
                  background: '#ff79c620', border: '1px solid #ff79c660', borderRadius: 8, padding: '10px 18px',
                  color: '#ff79c6', fontWeight: 700, fontSize: 13, fontFamily: font,
                  cursor: promoChecking ? 'wait' : 'pointer', opacity: promoInput.trim() ? 1 : 0.5,
                }}>{promoChecking ? 'Verific…' : 'Aplică'}</button>
                {(promoError || errors.promo_code) && (
                  <span style={{ flex: '1 1 100%', color: '#ff5555', fontSize: 12, fontFamily: font }}>{promoError || errors.promo_code}</span>
                )}
              </form>
            )}
          </div>
        </section>

        {/* ── 3. Contact ── */}
        <section>
          <StepTitle n={3} sub="Te sunăm în cel mult o zi lucrătoare.">Datele tale</StepTitle>
          <form onSubmit={submit} noValidate style={{
            background: '#111218', border: '1px solid #1e2030', borderRadius: 16, padding: 'clamp(18px,4vw,28px)',
          }}>
            <div className="order-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                ['name', 'Nume și prenume', true, 'text', 'name'],
                ['phone', 'Telefon', true, 'tel', 'tel'],
                ['email', 'Email', true, 'email', 'email'],
                ['business_name', 'Numele afacerii', false, 'text', 'organization'],
              ].map(([k, label, req, type, ac]) => (
                <Field key={k} label={label} required={req}>
                  <input className="order-input" type={type} autoComplete={ac} value={form[k]} onChange={set(k)}
                    style={{ ...inputStyle, borderColor: errors[k] ? '#ff5555' : '#282a36' }} />
                  {errors[k] && <span style={{ display: 'block', color: '#ff5555', fontSize: 12, marginTop: 4, fontFamily: font }}>{errors[k]}</span>}
                </Field>
              ))}
            </div>
            <div style={{ marginTop: 16 }}>
              <Field label="Ceva ce ar trebui să știm? (opțional)">
                <textarea className="order-input" rows={3} value={form.message} onChange={set('message')}
                  placeholder="Ex: am deja domeniu, vreau să apară meniul zilei, când ești disponibil să vorbim…"
                  style={{ ...inputStyle, resize: 'vertical' }} />
              </Field>
            </div>

            {/* Summary */}
            <div style={{
              marginTop: 20, background: '#0d0e12', border: '1px solid #1e2030', borderRadius: 12, padding: '14px 16px',
              fontFamily: font, fontSize: 13, color: '#8b8fa3', lineHeight: 1.8,
            }}>
              <div style={{ color: '#6272a4', fontFamily: mono, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>REZUMAT</div>
              <div>🎨 {choiceLabel}</div>
              <div>📦 {selectedPkg.name} — de la{' '}
                {q.pkgFinal < q.price && <s style={{ color: '#6272a4' }}>{selectedPkg.price}</s>}{' '}
                <span style={{ color: q.pkgFinal < q.price ? '#ff79c6' : '#f8f8f2' }}>{ron(q.pkgFinal)} RON</span>
              </div>
              {maintenance && (
                <div>🛠️ Mentenanță —{' '}
                  {q.maintFinal < MAINTENANCE_PRICE && <s style={{ color: '#6272a4' }}>150</s>}{' '}
                  <span style={{ color: q.maintFinal < MAINTENANCE_PRICE ? '#ff79c6' : '#f8f8f2' }}>{q.maintFinal} RON/lună</span>
                </div>
              )}
              {promo && q.applies && <div>🎁 Cod <span style={{ color: '#ff79c6', fontFamily: mono }}>{promo.code}</span> aplicat ({promo.label})</div>}
            </div>

            {errors.general && <p style={{ color: '#ff5555', fontSize: 13, fontFamily: font, margin: '14px 0 0' }}>{errors.general}</p>}
            {['submission', 'variant_number', 'catalog_key', 'package', 'promo_code', 'non_field_errors'].map(k => errors[k] && (
              <p key={k} style={{ color: '#ff5555', fontSize: 13, fontFamily: font, margin: '6px 0 0' }}>{errors[k]}</p>
            ))}

            <button type="submit" disabled={sending} style={{
              marginTop: 20, width: '100%', border: 'none', borderRadius: 12, padding: '16px 20px',
              background: sending ? '#282a36' : 'linear-gradient(135deg, #50fa7b, #8be9fd)', color: '#0d0e12',
              fontFamily: font, fontSize: 16, fontWeight: 700, cursor: sending ? 'wait' : 'pointer',
              boxShadow: sending ? 'none' : '0 8px 30px #50fa7b30',
            }}>
              {sending ? 'Se trimite…' : '🛒 Trimite comanda'}
            </button>
            <p style={{ textAlign: 'center', color: '#6272a4', fontSize: 12, fontFamily: font, margin: '12px 0 0' }}>
              Fără plată acum · Fără obligații până stabilim împreună detaliile
            </p>
          </form>
        </section>
      </div>

      {preview && (
        <FullscreenPreview
          title={preview.title}
          badge={preview.badge}
          html={preview.html}
          onClose={() => setPreview(null)}
          actionLabel={isChosen(preview.choice) ? '✓ Ales — mergi mai departe' : 'Alege acest model'}
          onAction={() => pick(preview.choice)}
        />
      )}
    </div>
  );
};

export default OrderPage;
